import React, { useEffect, useState } from 'react';
import {
  runnerApi, errorData, errorText, REGISTRY_TARGET,
  type Batch, type BatchOptions, type NonIdempotentFinding, type RolloutStep, type RunnerSettings, type S3Info,
} from '../../../api/migrationRunner';
import { Btn, Dialog, ErrorBox, Pill, shortName } from './ui';
import { useApprovalGate } from './ApprovalGate';

type BaseOptions = Required<Pick<BatchOptions, 'order' | 'stopOnFailure' | 'backup' | 'allowNonIdempotent'>>;

const targetLabel = (t: string) => (t === REGISTRY_TARGET ? 'Registry' : t.replace(/^cma_/, ''));

interface Props {
  open: boolean;
  onClose: () => void;
  /** What to run. */
  migrationIds: number[];
  targets: string[];
  /** Restrict to these migration × database pairs (e.g. only the not-run cells). */
  onlyPairs?: string[];
  title: string;
  onStarted: (batch: Batch) => void;
}

/**
 * "Apply N migrations to M databases": options, then a background batch.
 * Non-idempotent files are refused by the server (409) while the setting
 * blocks them; the dialog lists them and offers the explicit override.
 */
const BatchDialog: React.FC<Props> = ({ open, onClose, migrationIds, targets, onlyPairs, title, onStarted }) => {
  const [settings, setSettings] = useState<RunnerSettings | null>(null);
  const [s3, setS3] = useState<S3Info | null>(null);
  const [opts, setOpts] = useState<BaseOptions>({ order: 'tenant_first', stopOnFailure: true, backup: true, allowNonIdempotent: false });
  // Rollout in steps (approved mockup "Rollout in steps").
  const [inSteps, setInSteps] = useState(false);
  const [steps, setSteps] = useState<RolloutStep[] | null>(null);
  const [rolloutAvailable, setRolloutAvailable] = useState(true);
  const [pauseOnFailure, setPauseOnFailure] = useState(true);
  const [waitBetween, setWaitBetween] = useState(false);
  const gate = useApprovalGate();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [findings, setFindings] = useState<NonIdempotentFinding[] | null>(null);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setFindings(null);
    setInSteps(false);
    setSteps(null);
    runnerApi.settings()
      .then((r) => {
        setSettings(r.settings);
        setS3(r.s3);
        setOpts((o) => ({ ...o, stopOnFailure: r.settings.stop_on_failure, backup: r.settings.backup === 'always', allowNonIdempotent: false }));
      })
      .catch(() => setSettings(null));
  }, [open]);

  const turnOnSteps = async (on: boolean) => {
    setInSteps(on);
    if (!on || steps) return;
    try {
      const plan = await runnerApi.rolloutPlan(targets);
      setSteps(plan.steps);
      setRolloutAvailable(plan.available);
    } catch (err) {
      setError(errorText(err, 'Could not plan the steps'));
      setInSteps(false);
    }
  };

  /** Move one database to another step (or a new last step); empty steps disappear. */
  const moveTarget = (target: string, to: number) => {
    if (!steps) return;
    const next = steps.map((s) => ({ ...s, targets: s.targets.filter((t) => t !== target) }));
    if (to >= next.length) next.push({ label: null, targets: [target] });
    else next[to].targets.push(target);
    setSteps(next.filter((s) => s.targets.length));
  };

  const start = async () => {
    setBusy(true);
    setError(null);
    try {
      const rollout = inSteps && steps && steps.length > 1
        ? { steps, pauseOnFailure, waitMinutes: waitBetween ? 10 : 0 }
        : {};
      let created: Batch | null = null;
      // A batch on every company may need a second owner (Safety tab rule).
      const went = await gate.guard(async (approvalId) => {
        created = await runnerApi.createBatch({
          migrationIds, targets, options: { ...opts, ...rollout, ...(onlyPairs ? { onlyPairs } : {}) }, approvalId,
        });
      });
      if (went && created) onStarted(created);
    } catch (err) {
      const data = errorData<{ findings?: NonIdempotentFinding[]; mismatches?: unknown[] }>(err);
      if (data?.code === 'NON_IDEMPOTENT' && data.findings) {
        setFindings(data.findings);
      } else {
        setError(errorText(err, 'Could not start the batch'));
      }
    } finally {
      setBusy(false);
    }
  };

  const companies = targets.filter((t) => t !== '__registry__').length;
  const canSteps = companies >= 2;
  const hasRegistry = targets.includes('__registry__');

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={title}
      footer={
        <>
          <Btn kind="ghost" onClick={onClose}>Cancel</Btn>
          <Btn onClick={start} disabled={busy || (findings !== null && !opts.allowNonIdempotent) || (inSteps && (!steps || !rolloutAvailable))}>
            {busy ? 'Starting…' : 'Start in the background'}
          </Btn>
        </>
      }
    >
      <p>
        {onlyPairs ? `${onlyPairs.length} step${onlyPairs.length === 1 ? '' : 's'}: ` : ''}
        {migrationIds.length} migration{migrationIds.length === 1 ? '' : 's'} on{' '}
        {companies > 0 && <b>{companies} compan{companies === 1 ? 'y' : 'ies'}</b>}
        {companies > 0 && hasRegistry && ' and '}
        {hasRegistry && <b>the registry</b>}. Already-applied ones are skipped; companies with no database are skipped and listed.
      </p>
      <div className="space-y-2 rounded-xl border border-slate-200 p-3">
        <label className="flex items-start gap-2">
          <input type="checkbox" className="mt-0.5" checked={opts.backup} onChange={(e) => setOpts({ ...opts, backup: e.target.checked })} />
          <span>
            Back up each database first{' '}
            {s3 && (s3.configured ? <Pill tone="ok">S3</Pill> : <Pill tone="warn" title="BACKUP_S3_* is not set on the server">this server's disk only</Pill>)}
          </span>
        </label>
        <label className="flex items-start gap-2">
          <input type="checkbox" className="mt-0.5" checked={opts.order === 'tenant_first'} onChange={(e) => setOpts({ ...opts, order: e.target.checked ? 'tenant_first' : 'migration_first' })} />
          <span>One company at a time: all its migrations, then the next</span>
        </label>
        <label className="flex items-start gap-2">
          <input type="checkbox" className="mt-0.5" checked={!opts.stopOnFailure} onChange={(e) => setOpts({ ...opts, stopOnFailure: !e.target.checked })} />
          <span>Keep going after a failure <span className="text-slate-500">(stop is safer; keep going finishes the rest and lists failures)</span></span>
        </label>
        {canSteps && (
          <label className="flex items-start gap-2">
            <input type="checkbox" className="mt-0.5" checked={inSteps} onChange={(e) => turnOnSteps(e.target.checked)} />
            <span>Roll out in steps <span className="text-slate-500">(one small company, then one busy company, then everyone else)</span></span>
          </label>
        )}
      </div>
      {inSteps && steps && (
        <div className="space-y-2 rounded-xl border border-slate-200 p-3">
          {!rolloutAvailable && (
            <p className="text-[12.5px] text-amber-800">Steps need the registry migration migrations-2026-10-11-ops-approvals. Apply it first, or run without steps.</p>
          )}
          {steps.map((step, i) => (
            <div key={i} className="grid grid-cols-[14px_1fr] items-start gap-2">
              <span className="mt-1.5 h-2.5 w-2.5 rounded-full bg-slate-300" />
              <div className="min-w-0">
                <b className="text-slate-900">Step {i + 1}{step.label ? ` · ${step.label}` : ''}</b>{' '}
                <span className="text-xs text-slate-500">{step.targets.length} database{step.targets.length === 1 ? '' : 's'}</span>
                <div className="mt-1 flex flex-wrap gap-1.5">
                  {step.targets.map((t) => (
                    <span key={t} className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-slate-50 py-0.5 pl-2 pr-1 text-[12px]">
                      {targetLabel(t)}
                      <select
                        aria-label={`Move ${t} to another step`}
                        value={i}
                        onChange={(e) => moveTarget(t, Number(e.target.value))}
                        className="rounded border-0 bg-transparent py-0 text-[11px] text-slate-500"
                      >
                        {steps.map((_, j) => <option key={j} value={j}>step {j + 1}</option>)}
                        <option value={steps.length}>new step</option>
                      </select>
                    </span>
                  ))}
                </div>
              </div>
            </div>
          ))}
          <div className="flex flex-wrap gap-x-4 gap-y-1 pt-1 text-[13px]">
            <label className="flex items-center gap-2"><input type="checkbox" checked={pauseOnFailure} onChange={(e) => setPauseOnFailure(e.target.checked)} /> Pause automatically if any step fails</label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={waitBetween} onChange={(e) => setWaitBetween(e.target.checked)} /> Wait 10 min between steps</label>
          </div>
          <p className="text-xs text-slate-500">The next step starts after the one before passes. A paused batch waits for Continue on its page.</p>
        </div>
      )}
      {settings && (
        <p className="text-xs text-slate-500">
          Statement time limit {Math.round(settings.statement_timeout_s / 60)} min · lock wait {settings.lock_wait_s} s · one database at a time, locked while it runs.
          Safe to close this page: the batch keeps running on the server.
        </p>
      )}
      {findings && (
        <div className="space-y-2 rounded-xl border-[1.5px] border-red-400 bg-red-50 p-3">
          <b className="block text-red-800">Some statements can't run twice</b>
          <p className="text-[13px] text-red-900">
            They have no IF NOT EXISTS / IF EXISTS, so if anything fails a retry would fail on them. Fix the files if you can.
          </p>
          <ul className="max-h-48 space-y-1.5 overflow-auto text-[12.5px]">
            {findings.map((f) => (
              <li key={f.migrationId}>
                <b>{shortName(f.migration)}</b>
                {f.statements.slice(0, 4).map((s) => (
                  <div key={s.index} className="ml-3 font-mono text-[11.5px] text-slate-700">
                    {s.index + 1}: {s.sql.slice(0, 110)}{s.sql.length > 110 ? '…' : ''}
                  </div>
                ))}
                {f.statements.length > 4 && <div className="ml-3 text-slate-500">and {f.statements.length - 4} more</div>}
              </li>
            ))}
          </ul>
          <label className="flex items-start gap-2 text-[13px] font-semibold text-red-900">
            <input type="checkbox" className="mt-0.5" checked={opts.allowNonIdempotent} onChange={(e) => setOpts({ ...opts, allowNonIdempotent: e.target.checked })} />
            Run anyway — I understand a failure can't simply be retried
          </label>
        </div>
      )}
      {error && <ErrorBox>{error}</ErrorBox>}
      {gate.element}
    </Dialog>
  );
};

export default BatchDialog;
